import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '../../icons';
import { Badge, Btn, Card, MetricCard, Alert, SectionHeader, DataTable, Tabs, Modal, FormActions } from '../../components/ui';
import api from '../../api/axios';
import { toast } from 'react-hot-toast';
import { formatDateByPreference } from '../../utils/formatters';

export default function BillingScreen() {
  const [tab, setTab] = useState(0);
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [subHistory, setSubHistory] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [societyUnits, setSocietyUnits] = useState(1);
  const [societyName, setSocietyName] = useState('Society');
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  // Billing Cycle Toggle (Monthly vs Annual)
  const [isAnnual, setIsAnnual] = useState(true);

  // Modals & Actions state
  const [switchModal, setSwitchModal] = useState({ open: false, plan: null, type: null });
  const [downgradeTiming, setDowngradeTiming] = useState('next_cycle');
  const [switching, setSwitching] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTiming, setCancelTiming] = useState('next_cycle');
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Details & Invoice Modals
  const [selectedSubDetail, setSelectedSubDetail] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // Search & Filters for Subscription History
  const [subSearch, setSubSearch] = useState('');
  const [subStatusFilter, setSubStatusFilter] = useState('all');
  const [subCycleFilter, setSubCycleFilter] = useState('all');

  // Search & Filters for Payment History
  const [paymentSearch, setPaymentSearch] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
    loadRazorpay();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, subRes, paymentsRes] = await Promise.all([
        api.get('/api/v1/billing/plans'),
        api.get('/api/v1/billing/current-subscription'),
        api.get('/api/v1/billing/payments')
      ]);

      setPlans(plansRes.data || []);
      setCurrentSub(subRes.data?.current || null);
      setSubHistory(subRes.data?.history || []);
      setSocietyUnits(subRes.data?.society_units || 1);
      setSocietyName(subRes.data?.society_name || 'Society');
      setSummaryData(subRes.data?.summary || null);
      setPayments(paymentsRes.data || []);
    } catch (error) {
      toast.error('Failed to load billing details.');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpay = () => {
    if (window.Razorpay) return;
    const script = document.createElement('script');
    script.src = process.env.REACT_APP_RAZORPAY_CHECKOUT_URL || 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const handleSelectPlan = async (plan, force = false, timing = 'immediate') => {
    if (currentSub?.subscription_plan_id === plan.id) return;

    if (currentSub && !force) {
      const currentPlan = plans.find(p => p.id === currentSub.subscription_plan_id);
      const isUpgrade = parseFloat(plan.price_yearly) > parseFloat(currentPlan?.price_yearly || 0);
      setSwitchModal({ open: true, plan, type: isUpgrade ? 'upgrade' : 'downgrade' });
      setDowngradeTiming('next_cycle');
      return;
    }

    setProcessingId(plan.id);
    try {
      const cycle = isAnnual ? 'yearly' : 'monthly';
      const res = await api.post('/api/v1/billing/create-subscription', {
        plan_id: plan.id,
        billing_cycle: cycle,
        timing: timing
      });

      const options = {
        key: res.data.key,
        subscription_id: res.data.subscription_id,
        name: "The Clover App",
        description: `Subscription: ${plan.name} (${cycle})`,
        handler: async (response) => {
          try {
            await api.post('/api/v1/billing/verify-subscription', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: plan.id,
              billing_cycle: cycle,
              timing: timing
            });
            toast.success('Subscription activated successfully!');
            fetchData();
          } catch (err) {
            toast.error(err.message || 'Subscription verification failed.');
          }
        },
        prefill: {
          name: localStorage.getItem('user_name') || '',
          email: localStorage.getItem('user_email') || '',
        },
        theme: {
          color: "#4f46e5"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to initialize subscription checkout.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelConfirm = async () => {
    setCancelling(true);
    try {
      const res = await api.post('/api/v1/billing/cancel-subscription', {
        timing: cancelTiming
      });
      toast.success(res.data?.message || 'Subscription cancelled successfully.');
      setCancelModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to cancel subscription.');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await api.post('/api/v1/billing/reactivate-subscription');
      toast.success('Subscription reactivated successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to reactivate subscription.');
    } finally {
      setReactivating(false);
    }
  };

  const handleSwitchConfirm = async () => {
    const timing = switchModal.type === 'downgrade' ? downgradeTiming : 'immediate';
    const planToSwitchTo = switchModal.plan;
    setSwitchModal({ open: false, plan: null, type: null });
    handleSelectPlan(planToSwitchTo, true, timing);
  };

  const handleViewInvoice = async (payment) => {
    setLoadingInvoice(true);
    try {
      const res = await api.get(`/api/v1/billing/invoices/${payment.id}`);
      setSelectedInvoice(res.data);
    } catch (err) {
      toast.error('Failed to load invoice details.');
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleRetryPayment = (payment) => {
    const plan = plans.find(p => p.name === payment.plan_name || p.slug === 'gold');
    if (plan) {
      handleSelectPlan(plan, true, 'immediate');
    } else if (plans.length > 0) {
      handleSelectPlan(plans[1] || plans[0], true, 'immediate');
    }
  };

  // CSV Export for Subscriptions
  const exportSubscriptionsCSV = () => {
    if (!subHistory || subHistory.length === 0) {
      toast.error('No subscription data to export.');
      return;
    }
    const headers = ['Subscription ID', 'Plan Name', 'Billing Cycle', 'Units', 'Base Amount (INR)', 'GST 18% (INR)', 'Total Amount (INR)', 'Start Date', 'End / Renewal Date', 'Status'];
    const rows = filteredSubscriptions.map(sub => [
      sub.invoice_ref || `SUB-${sub.id}`,
      sub.plan?.name || '-',
      sub.billing_cycle || 'YEARLY',
      sub.billable_units || sub.current_units || 1,
      sub.base_amount || '0.00',
      sub.tax_amount || '0.00',
      sub.total_amount || '0.00',
      sub.starts_at ? new Date(sub.starts_at).toISOString().split('T')[0] : '-',
      sub.ends_at ? new Date(sub.ends_at).toISOString().split('T')[0] : '-',
      sub.status || '-'
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `subscriptions_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Subscription history exported to CSV.');
  };

  // CSV Export for Payments
  const exportPaymentsCSV = () => {
    if (!payments || payments.length === 0) {
      toast.error('No payment data to export.');
      return;
    }
    const headers = ['Payment ID', 'Invoice Number', 'Payment Date', 'Plan', 'Billing Period', 'Base Amount (INR)', 'GST 18% (INR)', 'Total Paid (INR)', 'Payment Method', 'Status'];
    const rows = filteredPayments.map(p => [
      p.razorpay_payment_id || `PAY-${p.id}`,
      p.invoice_number || '-',
      p.created_at ? new Date(p.created_at).toISOString().split('T')[0] : '-',
      p.plan_name || '-',
      p.billing_period || 'YEARLY',
      p.base_amount || '0.00',
      p.tax_amount || '0.00',
      p.amount || '0.00',
      `"${p.payment_method || 'Online'}"`,
      p.status || '-'
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `payments_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Payment history exported to CSV.');
  };

  // Filtered Subscriptions
  const filteredSubscriptions = useMemo(() => {
    return subHistory.filter(sub => {
      const matchSearch = subSearch.trim() === '' || 
        (sub.invoice_ref && sub.invoice_ref.toLowerCase().includes(subSearch.toLowerCase())) ||
        (sub.plan?.name && sub.plan.name.toLowerCase().includes(subSearch.toLowerCase())) ||
        (sub.id && String(sub.id).includes(subSearch));
      
      const matchStatus = subStatusFilter === 'all' || sub.status === subStatusFilter;
      const matchCycle = subCycleFilter === 'all' || sub.billing_cycle?.toLowerCase() === subCycleFilter.toLowerCase();

      return matchSearch && matchStatus && matchCycle;
    });
  }, [subHistory, subSearch, subStatusFilter, subCycleFilter]);

  // Filtered Payments
  const filteredPayments = useMemo(() => {
    return payments.filter(p => {
      const matchSearch = paymentSearch.trim() === '' || 
        (p.razorpay_payment_id && p.razorpay_payment_id.toLowerCase().includes(paymentSearch.toLowerCase())) ||
        (p.invoice_number && p.invoice_number.toLowerCase().includes(paymentSearch.toLowerCase())) ||
        (p.plan_name && p.plan_name.toLowerCase().includes(paymentSearch.toLowerCase()));

      const matchStatus = paymentStatusFilter === 'all' || p.status === paymentStatusFilter;

      return matchSearch && matchStatus;
    });
  }, [payments, paymentSearch, paymentStatusFilter]);

  if (loading && plans.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isScheduledForCancel = summaryData?.is_scheduled_for_cancel || (currentSub?.canceled_at && currentSub?.ends_at && new Date(currentSub.ends_at) > new Date());
  const isCanceled = currentSub?.status === 'canceled' || currentSub?.status === 'cancelled';
  const isExpired = currentSub?.status === 'expired';

  return (
    <div className="fade-up space-y-6 pb-16 sm:pb-8">
      {/* 1. HERO ACTIVE SUBSCRIPTION STATUS BANNER */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 sm:p-8 shadow-xl border border-indigo-900/40">
        <div className="absolute top-0 right-0 -mt-12 -mr-12 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-3 max-w-2xl">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs uppercase tracking-widest text-indigo-300 font-bold bg-indigo-500/20 px-3 py-1 rounded-full border border-indigo-400/30">
                {societyName}
              </span>
              {currentSub ? (
                <Badge color={isScheduledForCancel ? 'yellow' : (isCanceled ? 'red' : (isExpired ? 'gray' : 'green'))} dot>
                  {isScheduledForCancel ? 'Cancellation Scheduled' : (isCanceled ? 'Canceled' : (isExpired ? 'Expired' : 'Active Subscription'))}
                </Badge>
              ) : (
                <Badge color="blue" dot>Free Trial / Basic Plan</Badge>
              )}
            </div>

            <div>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
                {currentSub?.plan?.name || 'Free Community Plan'}
              </h1>
              <p className="text-sm text-slate-300 mt-1">
                Currently managing <strong className="text-white font-semibold">{societyUnits} billable society units</strong>.
                {currentSub?.ends_at && (
                  <span>
                    {' '}• Next billing / renewal on <strong className="text-indigo-200">{formatDateByPreference(currentSub.ends_at)}</strong>.
                  </span>
                )}
              </p>
            </div>

            {isScheduledForCancel && (
              <div className="flex items-center gap-2 text-xs bg-amber-500/20 border border-amber-400/30 text-amber-200 px-3.5 py-2 rounded-lg">
                <Icon name="alert-triangle" size={16} className="text-amber-400 flex-shrink-0" />
                <span>Your subscription will remain active until {formatDateByPreference(currentSub?.ends_at)}. You will lose access after that date unless reactivated.</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {isScheduledForCancel || (isCanceled && currentSub?.ends_at && new Date(currentSub.ends_at) > new Date()) ? (
              <button
                onClick={handleReactivate}
                disabled={reactivating}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-900/30 transition-all flex items-center gap-2 text-sm"
              >
                {reactivating ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Icon name="check-circle" size={16} />}
                Reactivate Subscription
              </button>
            ) : null}

            {currentSub && !isCanceled && !isScheduledForCancel && (
              <button
                onClick={() => setCancelModalOpen(true)}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-300 hover:text-red-200 border border-red-500/30 text-xs font-semibold rounded-xl transition-all"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 2. ANNUAL SUMMARY METRIC CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Spent This FY"
          value={`₹${(summaryData?.total_spent_fy || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
          sub="April 1 to Present"
          icon="credit-card"
          color="indigo"
        />
        <MetricCard
          label="Active Society Units"
          value={societyUnits}
          sub="Billable quantity"
          icon="building"
          color="blue"
        />
        <MetricCard
          label="Invoices Generated"
          value={summaryData?.total_invoices_count || payments.length || 0}
          sub="Paid & captured invoices"
          icon="file-text"
          color="emerald"
        />
        <MetricCard
          label="Current Tier"
          value={currentSub?.plan?.name || 'Free'}
          sub={currentSub?.billing_cycle ? `${currentSub.billing_cycle} Billing` : 'No active cycle'}
          icon="check-circle"
          color="purple"
        />
      </div>

      {/* 3. TABS NAVIGATION */}
      <Tabs
        tabs={["Plans & Pricing", "Subscription History", "Payment History"]}
        active={tab}
        onChange={setTab}
      />

      {/* TAB 0: PLANS & PRICING */}
      {tab === 0 && (
        <div className="space-y-8 pb-10 sm:pb-2">
          {/* Monthly / Annual Toggle Switch */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 bg-white dark:bg-slate-900 rounded-2xl border border-border dark:border-slate-800 shadow-sm gap-4">
            <div>
              <h2 className="text-lg font-bold text-ink dark:text-slate-100">Choose Your Billing Plan</h2>
              <p className="text-xs text-muted dark:text-slate-400 mt-0.5">
                Pricing dynamically scales with your total society units ({societyUnits} units).
              </p>
            </div>

            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-bold text-indigo-700 dark:text-indigo-300">
              <Icon name="calendar" size={14} />
              <span>Annual Billing Cycle</span>
            </div>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {(plans || []).map((pl) => {
              const isActive = currentSub?.subscription_plan_id === pl.id && currentSub?.status === 'active';
              const unitPrice = isAnnual ? parseFloat(pl.price_yearly || 0) : parseFloat(pl.price_monthly || 0);
              const isFree = pl.slug === 'free' || unitPrice === 0;

              // Calculation based on units
              const multiplier = 1;
              const baseAmount = isFree ? 0 : (unitPrice * societyUnits * multiplier);
              const taxAmount = isFree ? 0 : Math.round(baseAmount * 0.18);
              const totalAmount = baseAmount + taxAmount;

              const isUnitExceeded = pl.unit_limit !== -1 && societyUnits > pl.unit_limit;
              const currentPlan = (plans || []).find(p => p.id === currentSub?.subscription_plan_id);
              const currentPrice = currentPlan ? (isAnnual ? parseFloat(currentPlan.price_yearly) : parseFloat(currentPlan.price_monthly)) : 0;
              const isUpgrade = unitPrice > currentPrice;

              let btnLabel = isFree ? "Free Plan" : (isAnnual ? "Start Annual Plan" : "Start Monthly Plan");
              if (isActive) {
                btnLabel = "Current Active Plan";
              } else if (currentPlan) {
                btnLabel = isUpgrade ? `Upgrade to ${pl.name}` : `Downgrade to ${pl.name}`;
              }

              return (
                <div
                  key={pl.id}
                  className={`relative rounded-2xl p-6 border-2 flex flex-col transition-all duration-200 bg-white dark:bg-slate-900 ${
                    isActive
                      ? "border-primary ring-4 ring-primary/10 md:scale-[1.02] shadow-xl"
                      : pl.slug === 'gold_plus'
                      ? "border-indigo-400 dark:border-indigo-600 shadow-md hover:shadow-xl"
                      : "border-border dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 shadow-sm"
                  } ${isUnitExceeded ? "opacity-60" : ""}`}
                >
                  {pl.slug === 'gold_plus' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white text-[11px] font-black uppercase tracking-wider px-3.5 py-0.5 rounded-full shadow-md">
                      Most Popular
                    </div>
                  )}

                  <div className="mb-4">
                    <div className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-1">
                      {pl.name}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-black text-ink dark:text-slate-100">
                        ₹{unitPrice}
                      </span>
                      <span className="text-xs text-muted dark:text-slate-400 font-medium">
                        {isFree ? '/ month' : (isAnnual ? '/ unit / year' : '/ unit / month')}
                      </span>
                    </div>
                    <div className="text-xs text-muted dark:text-slate-400 mt-1">
                      {pl.unit_limit === -1 ? 'Unlimited Units' : `Up to ${pl.unit_limit} units`} • {pl.admin_limit === -1 ? 'Unlimited Admins' : `Up to ${pl.admin_limit} admins`}
                    </div>
                  </div>

                  {/* Calculated Price Breakdown Box */}
                  <div className="mb-6 p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-800 text-xs space-y-1.5">
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>Base ({societyUnits} units)</span>
                      <span className="font-semibold">₹{baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-slate-500 dark:text-slate-400">
                      <span>GST (18%)</span>
                      <span>₹{taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="pt-1.5 border-t border-slate-200 dark:border-slate-700 flex justify-between font-bold text-slate-900 dark:text-white text-sm">
                      <span>Total Payable</span>
                      <span className="text-indigo-600 dark:text-indigo-400">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-2.5 mb-8 flex-1">
                    {(pl.features || []).map((feat, j) => (
                      <div key={j} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
                        <Icon name="check" size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{typeof feat === 'string' ? feat : feat.name}</span>
                      </div>
                    ))}
                  </div>

                  {/* Action Button */}
                  <div className="mt-auto pt-3">
                    {isUnitExceeded ? (
                      <div className="text-center p-2.5 bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 text-xs font-semibold rounded-xl border border-red-200 dark:border-red-900">
                        Exceeds Society Units ({societyUnits} &gt; {pl.unit_limit})
                      </div>
                    ) : (
                      <Btn
                        full
                        variant={isActive ? "default" : (isUpgrade ? "primary" : "secondary")}
                        loading={processingId === pl.id}
                        disabled={isActive || isFree || (processingId !== null && processingId !== pl.id)}
                        onClick={() => handleSelectPlan(pl)}
                      >
                        {btnLabel}
                      </Btn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB 1: SUBSCRIPTION HISTORY */}
      {tab === 1 && (
        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <SectionHeader title="Subscription History" subtitle="Complete record of your society's plan cycles, upgrades, and changes." />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exportSubscriptionsCSV}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 border border-slate-300 dark:border-slate-700"
              >
                <Icon name="download" size={14} />
                Export CSV
              </button>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            <div>
              <input
                type="text"
                placeholder="Search by Sub ID / Plan..."
                value={subSearch}
                onChange={(e) => setSubSearch(e.target.value)}
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <select
                value={subStatusFilter}
                onChange={(e) => setSubStatusFilter(e.target.value)}
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="canceled">Canceled</option>
                <option value="expired">Expired</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <select
                value={subCycleFilter}
                onChange={(e) => setSubCycleFilter(e.target.value)}
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="all">All Billing Cycles</option>
                <option value="yearly">Yearly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {filteredSubscriptions.length > 0 ? (
            <DataTable
              cols={["Subscription ID", "Plan", "Cycle", "Units", "Base (INR)", "GST (18%)", "Total (INR)", "Start Date", "Renews / Ends", "Status", "Actions"]}
              rows={filteredSubscriptions.map(sub => [
                <span className="font-mono text-xs font-bold text-slate-700 dark:text-slate-300">{sub.invoice_ref || `SUB-${sub.id}`}</span>,
                <span className="font-bold text-indigo-600 dark:text-indigo-400 uppercase text-xs tracking-wider">{sub.plan?.name || '-'}</span>,
                <Badge color={sub.billing_cycle === 'YEARLY' ? 'purple' : 'blue'}>{sub.billing_cycle || 'MONTHLY'}</Badge>,
                <span className="text-xs font-semibold">{sub.billable_units || sub.current_units || 1}</span>,
                `₹${(sub.base_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                `₹${(sub.tax_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                <span className="font-bold text-slate-900 dark:text-white">₹{(sub.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>,
                formatDateByPreference(sub.starts_at),
                sub.ends_at ? formatDateByPreference(sub.ends_at) : (sub.canceled_at ? formatDateByPreference(sub.canceled_at) : '-'),
                <Badge color={sub.status === 'active' ? 'green' : (sub.status === 'canceled' || sub.status === 'cancelled' ? 'red' : 'gray')} dot>{sub.status}</Badge>,
                <button
                  onClick={() => setSelectedSubDetail(sub)}
                  className="px-2 py-1 text-xs bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-bold rounded hover:bg-indigo-100 transition-colors"
                >
                  Details
                </button>
              ])}
            />
          ) : (
            <div className="p-12 text-center text-muted dark:text-slate-400">
              <Icon name="file-text" size={48} className="mx-auto mb-3 opacity-20" />
              <p className="font-medium text-sm">No subscription records match your search.</p>
            </div>
          )}
        </Card>
      )}

      {/* TAB 2: PAYMENT HISTORY */}
      {tab === 2 && (
        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <SectionHeader title="Payment History & Invoices" subtitle="Download GST tax invoices and view online transaction receipts." />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exportPaymentsCSV}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 border border-slate-300 dark:border-slate-700"
              >
                <Icon name="download" size={14} />
                Export CSV
              </button>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            <div>
              <input
                type="text"
                placeholder="Search by Payment ID / Invoice # / Plan..."
                value={paymentSearch}
                onChange={(e) => setPaymentSearch(e.target.value)}
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="all">All Payment Statuses</option>
                <option value="captured">Captured (Paid)</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>

          {filteredPayments.length > 0 ? (
            <DataTable
              cols={["Payment / Txn ID", "Invoice #", "Date", "Plan", "Base (INR)", "GST (18%)", "Total Paid", "Method", "Status", "Actions"]}
              rows={filteredPayments.map(p => [
                <span className="font-mono text-xs text-slate-700 dark:text-slate-300">{p.razorpay_payment_id || `PAY-${p.id}`}</span>,
                <span className="font-mono text-xs font-bold text-indigo-600 dark:text-indigo-400">{p.invoice_number}</span>,
                formatDateByPreference(p.created_at),
                <span className="text-xs font-bold">{p.plan_name}</span>,
                `₹${(p.base_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                `₹${(p.tax_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                <span className="font-black text-slate-900 dark:text-white">₹{parseFloat(p.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>,
                <span className="text-xs text-slate-500 dark:text-slate-400">{p.payment_method}</span>,
                <Badge color={p.status === 'captured' ? 'green' : (p.status === 'failed' ? 'red' : 'blue')} dot>{p.status}</Badge>,
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleViewInvoice(p)}
                    className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 text-xs font-bold rounded transition-colors flex items-center gap-1"
                  >
                    <Icon name="file-text" size={12} />
                    Invoice
                  </button>
                  {p.status === 'failed' && (
                    <button
                      onClick={() => handleRetryPayment(p)}
                      className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded transition-colors"
                    >
                      Retry
                    </button>
                  )}
                </div>
              ])}
            />
          ) : (
            <div className="p-12 text-center text-muted dark:text-slate-400">
              <Icon name="credit-card" size={48} className="mx-auto mb-3 opacity-20" />
              <p className="font-medium text-sm">No payment records found.</p>
            </div>
          )}
        </Card>
      )}

      {/* MODAL 1: PLAN SWITCH (UPGRADE / DOWNGRADE) */}
      {switchModal.open && switchModal.plan && (
        <Modal
          onClose={() => !switching && setSwitchModal({ open: false, plan: null, type: null })}
          title={`Confirm Plan ${switchModal.type === 'upgrade' ? 'Upgrade' : 'Downgrade'}`}
        >
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
              <div className="text-center flex-1">
                <div className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">Current Plan</div>
                <div className="text-lg font-black text-slate-800 dark:text-slate-200 uppercase">
                  {plans.find(p => p.id === currentSub?.subscription_plan_id)?.name || 'Current'}
                </div>
              </div>
              <div className="px-4 text-slate-400">
                <Icon name="arrow-right" size={24} />
              </div>
              <div className="text-center flex-1">
                <div className="text-xs text-primary uppercase font-bold tracking-wider mb-1">New Plan</div>
                <div className="text-lg font-black text-primary uppercase">{switchModal.plan.name}</div>
              </div>
            </div>

            <div className="p-4 bg-primary/5 rounded-xl border border-primary/10 space-y-2">
              <div className="flex justify-between text-xs text-slate-600 dark:text-slate-300">
                <span>Unit Rate ({isAnnual ? 'Annual' : 'Monthly'})</span>
                <span className="font-bold">₹{isAnnual ? switchModal.plan.price_yearly : switchModal.plan.price_monthly} / unit</span>
              </div>
              <div className="flex justify-between text-xs text-slate-600 dark:text-slate-300">
                <span>Total Society Units</span>
                <span className="font-bold">{societyUnits} units</span>
              </div>
              <div className="pt-2 border-t border-primary/10 flex justify-between font-black text-primary text-base">
                <span>Total Due ({isAnnual ? 'Yearly' : 'Monthly'})</span>
                <span>
                  ₹{((isAnnual ? switchModal.plan.price_yearly : switchModal.plan.price_monthly) * societyUnits * 1.18).toFixed(2)}
                  <span className="text-xs font-normal text-slate-500"> (incl. 18% GST)</span>
                </span>
              </div>
            </div>

            {switchModal.type === 'downgrade' && (
              <div className="space-y-3">
                <div className="text-sm font-bold text-slate-800 dark:text-slate-200">When should this downgrade take effect?</div>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${downgradeTiming === 'next_cycle' ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}>
                  <input type="radio" className="mt-1" checked={downgradeTiming === 'next_cycle'} onChange={() => setDowngradeTiming('next_cycle')} />
                  <div>
                    <div className="text-sm font-bold text-slate-800 dark:text-slate-200">Next Billing Cycle (Recommended)</div>
                    <div className="text-xs text-slate-500 mt-0.5">Retain all current features until the end of your billing cycle.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${downgradeTiming === 'immediate' ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}>
                  <input type="radio" className="mt-1" checked={downgradeTiming === 'immediate'} onChange={() => setDowngradeTiming('immediate')} />
                  <div>
                    <div className="text-sm font-bold text-slate-800 dark:text-slate-200">Immediately</div>
                    <div className="text-xs text-slate-500 mt-0.5">Switch immediately to the lower plan limit.</div>
                  </div>
                </label>
              </div>
            )}

            <Alert type="info">
              <div className="text-xs">
                You will be redirected to the secure Razorpay payment gateway to authorize the new subscription schedule.
              </div>
            </Alert>

            <FormActions
              onSave={handleSwitchConfirm}
              onCancel={() => setSwitchModal({ open: false, plan: null, type: null })}
              loading={switching}
              label={`Proceed to ${switchModal.type === 'upgrade' ? 'Upgrade' : 'Downgrade'}`}
            />
          </div>
        </Modal>
      )}

      {/* MODAL 2: CANCEL SUBSCRIPTION CONFIRMATION */}
      {cancelModalOpen && (
        <Modal
          onClose={() => !cancelling && setCancelModalOpen(false)}
          title="Cancel Active Subscription"
        >
          <div className="space-y-6">
            <Alert type="warning">
              <strong>Are you sure you want to cancel?</strong> Your society will lose access to premium automated billing, security gates, emergency broadcasts, and advance reports once the subscription expires.
            </Alert>

            <div className="space-y-3">
              <div className="text-sm font-bold text-slate-800 dark:text-slate-200">Select Cancellation Effective Date:</div>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cancelTiming === 'next_cycle' ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700'}`}>
                <input type="radio" className="mt-1" checked={cancelTiming === 'next_cycle'} onChange={() => setCancelTiming('next_cycle')} />
                <div>
                  <div className="text-sm font-bold text-slate-800 dark:text-slate-200">At End of Current Billing Cycle (Recommended)</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Maintain full access until {formatDateByPreference(currentSub?.ends_at)}. No auto-renewal charges will occur.
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cancelTiming === 'immediate' ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700'}`}>
                <input type="radio" className="mt-1" checked={cancelTiming === 'immediate'} onChange={() => setCancelTiming('immediate')} />
                <div>
                  <div className="text-sm font-bold text-slate-800 dark:text-slate-200">Cancel Immediately</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Revoke premium entitlements right away.
                  </div>
                </div>
              </label>
            </div>

            <FormActions
              onSave={handleCancelConfirm}
              onCancel={() => setCancelModalOpen(false)}
              loading={cancelling}
              label="Confirm Cancellation"
              saveBtnProps={{ className: "!bg-red-600 hover:!bg-red-700 !text-white" }}
            />
          </div>
        </Modal>
      )}

      {/* MODAL 3: SUBSCRIPTION DETAILS MODAL */}
      {selectedSubDetail && (
        <Modal
          onClose={() => setSelectedSubDetail(null)}
          title={`Subscription Details - ${selectedSubDetail.invoice_ref || selectedSubDetail.id}`}
        >
          <div className="space-y-4 text-xs">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Plan Name</span>
                <span className="font-bold uppercase text-indigo-600 dark:text-indigo-400">{selectedSubDetail.plan?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Billing Cycle</span>
                <span className="font-semibold uppercase text-slate-800 dark:text-slate-200">{selectedSubDetail.billing_cycle}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Status</span>
                <Badge color={selectedSubDetail.status === 'active' ? 'green' : 'gray'}>{selectedSubDetail.status}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Billable Units</span>
                <span className="font-semibold text-slate-800 dark:text-slate-200">{selectedSubDetail.billable_units || selectedSubDetail.current_units || 1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Start Date</span>
                <span className="text-slate-800 dark:text-slate-200">{formatDateByPreference(selectedSubDetail.starts_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">End / Renewal Date</span>
                <span className="text-slate-800 dark:text-slate-200">{formatDateByPreference(selectedSubDetail.ends_at)}</span>
              </div>
            </div>

            <div className="p-4 bg-indigo-50/50 dark:bg-indigo-950/30 rounded-xl space-y-1.5 border border-indigo-100 dark:border-indigo-900/50">
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Base Amount</span>
                <span className="font-medium text-slate-800 dark:text-slate-200">₹{(selectedSubDetail.base_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>GST (18%)</span>
                <span className="font-medium text-slate-800 dark:text-slate-200">₹{(selectedSubDetail.tax_amount || 0).toFixed(2)}</span>
              </div>
              <div className="pt-2 border-t border-indigo-200 dark:border-indigo-900 flex justify-between font-bold text-sm text-indigo-900 dark:text-indigo-200">
                <span>Total Amount</span>
                <span>₹{(selectedSubDetail.total_amount || 0).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setSelectedSubDetail(null)}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white font-semibold rounded-lg transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* MODAL 4: INVOICE / RECEIPT MODAL */}
      {selectedInvoice && (
        <Modal
          onClose={() => setSelectedInvoice(null)}
          title={`Tax Invoice - ${selectedInvoice.invoice_number}`}
        >
          <div className="p-6 bg-white dark:bg-slate-900 rounded-xl space-y-6 text-xs text-slate-700 dark:text-slate-300" id="printable-invoice">
            {/* Header */}
            <div className="flex justify-between items-start border-b border-slate-200 dark:border-slate-700 pb-4">
              <div>
                <h3 className="text-lg font-black text-indigo-600 dark:text-indigo-400">{selectedInvoice.company_name}</h3>
                <p className="text-slate-500 dark:text-slate-400 mt-1 max-w-xs">{selectedInvoice.company_address}</p>
                <p className="font-mono text-[11px] mt-1"><strong>GSTIN:</strong> {selectedInvoice.company_gstin}</p>
              </div>
              <div className="text-right">
                <span className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 font-bold px-2.5 py-1 rounded text-[11px] uppercase">
                  {selectedInvoice.status}
                </span>
                <p className="text-sm font-black text-slate-900 dark:text-white mt-2">{selectedInvoice.invoice_number}</p>
                <p className="text-slate-500">{selectedInvoice.invoice_date}</p>
              </div>
            </div>

            {/* Bill To */}
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="text-[10px] font-bold uppercase text-slate-400">Billed To:</div>
              <div className="font-bold text-slate-900 dark:text-white text-sm mt-0.5">{selectedInvoice.society_name}</div>
              <div className="text-slate-500">{selectedInvoice.society_address}</div>
            </div>

            {/* Line Items */}
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-slate-400 text-[10px] uppercase">
                  <th className="py-2">Description</th>
                  <th className="py-2 text-center">Units</th>
                  <th className="py-2 text-right">Base Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                <tr>
                  <td className="py-3">
                    <div className="font-bold text-slate-900 dark:text-white">Society Subscription Plan - {selectedInvoice.plan_name}</div>
                    <div className="text-slate-500 text-[11px]">{selectedInvoice.billing_cycle} Recurring Billing Cycle</div>
                  </td>
                  <td className="py-3 text-center">{selectedInvoice.billable_units}</td>
                  <td className="py-3 text-right font-semibold">₹{(selectedInvoice.base_amount || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {/* Totals */}
            <div className="space-y-1.5 pt-4 border-t border-slate-200 dark:border-slate-700 text-right">
              <div className="flex justify-between">
                <span className="text-slate-500">Subtotal:</span>
                <span className="font-semibold">₹{(selectedInvoice.base_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">GST (18%):</span>
                <span>₹{(selectedInvoice.tax_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base font-black text-indigo-600 dark:text-indigo-400 pt-2 border-t border-slate-200 dark:border-slate-700">
                <span>Grand Total:</span>
                <span>₹{(selectedInvoice.total_amount || 0).toFixed(2)}</span>
              </div>
            </div>

            {/* Footer / Actions */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-200 dark:border-slate-700">
              <span className="font-mono text-[10px] text-slate-400">Payment Ref: {selectedInvoice.payment_id}</span>
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors flex items-center gap-1.5 text-xs shadow-md"
              >
                <Icon name="printer" size={14} />
                Print / Save PDF
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

